import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { get as _get } from "lodash";
import { Box, Button, Chip, InputAdornment, List, Modal, TextField, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import TuneIcon from "@mui/icons-material/Tune";
import { getActiveAdvertisement } from "../../../utils/advertisement";

// components mui

// Actions
import { setModalData, setModal } from "../../../redux/actions/modals";
import { setTokenReceived, setTokenSend } from "../../../redux/actions/swap";
import { setAssetOne, setAssetTwo } from "../../../redux/actions/liquidity";
import { setImportAssetOne, setImportAssetTwo } from "../../../redux/actions/positions";

// components
import ModalHeader from "../ModalHeader";
import TokenLogo from "../../TokenLogo";
import TokenListItem from "../../TokenListItem";
import { advertisementSlots } from "../../../constants/advertisement";
import { CONFIG_BASE } from "../../../constants/configs";

// a token matches by name, symbol or its exact address (pasted)
const matchesSearch = (token, text) => {
  const value = text.trim().toLowerCase();
  return (
    _get(token, "name", "").toLowerCase().includes(value) ||
    _get(token, "symbol", "").toLowerCase().includes(value) ||
    _get(token, "address", "").toLowerCase() === value
  );
};

const ModalSelectToken = () => {
  // hooks
  const dispatch = useDispatch();

  // selectors
  const currentModal = useSelector((state) => state.modals.modal);
  const currentModalData = useSelector((state) => state.modals.data);
  // swap
  const tokenSend = useSelector((state) => state.swap.send);
  const tokenReceived = useSelector((state) => state.swap.received);
  // liquidity
  const assetOne = useSelector((state) => state.liquidity.assetOne);
  const assetTwo = useSelector((state) => state.liquidity.assetTwo);
  // positions
  const importAssetOne = useSelector((state) => state.positions.assetOne);
  const importAssetTwo = useSelector((state) => state.positions.assetTwo);
  const base_tokens = useSelector((state) => state.tokens.base_tokens);
  const custom_tokens = useSelector((state) => state.tokens.custom_tokens);

  // states
  const [inputSearch, setInputSearch] = useState(null);
  const [tokens, setTokens] = useState([]);

  // console.log(currentModalData)

  // functions
  const search = (e) => {
    const value = e.target.value;
    setInputSearch(value);
    if (value.length > 0) {
      setTokens(base_tokens.concat(custom_tokens).filter((el) => matchesSearch(el, value)));
    } else {
      setTokens(base_tokens.concat(custom_tokens));
      setInputSearch(null);
    }
  };
  const closeModal = () => {
    dispatch(setModal(null));
    dispatch(setModalData(null));
  };

  const AdvertisementSlot = () => {
    let ad = getActiveAdvertisement(advertisementSlots.searchbar);
    if (!ad) {
      return null;
    }
    if (ad.length > 0) {
      ad = ad[0];
    }
    // take token from standard token list
    let advertisedToken = tokens.filter((t) => {
      return t.address == ad.contract;
    });

    if (advertisedToken.length > 0) {
      advertisedToken = advertisedToken[0];
    } else {
      return null;
    }
    return <TokenListItem token={advertisedToken} highlighted onClick={(e) => onSelect(e, advertisedToken)} />;
  };

  const onSelect = (e, token) => {
    const reduce = _get(currentModalData, "reduce", "");
    if (reduce === "swap" || reduce === "liquidity" || reduce === "position") {
      const value = _get(currentModalData, "value", "");
      switch (value) {
        case "send":
          if (_get(token, "address", "") === _get(tokenReceived, "address", "")) {
            dispatch(setTokenSend(tokenReceived));
            dispatch(setTokenReceived(tokenSend));
          } else {
            dispatch(setTokenSend(token));
          }
          closeModal();
          break;
        case "received":
          if (_get(token, "address", "") === _get(tokenSend, "address", "")) {
            dispatch(setTokenSend(tokenReceived));
            dispatch(setTokenReceived(tokenSend));
          } else {
            dispatch(setTokenReceived(token));
          }
          closeModal();
          break;
        case "assetOne":
          if (_get(token, "address", "") === _get(assetTwo, "address", "")) {
            dispatch(setAssetOne(assetTwo));
            dispatch(setAssetTwo(assetOne));
          } else {
            dispatch(setAssetOne(token));
          }
          closeModal();
          break;
        case "assetTwo":
          if (_get(token, "address", "") === _get(assetOne, "address", "")) {
            dispatch(setAssetOne(assetTwo));
            dispatch(setAssetTwo(assetOne));
          } else {
            dispatch(setAssetTwo(token));
          }
          closeModal();
          break;
        case "importAssetOne":
          if (_get(token, "address", "") === _get(importAssetTwo, "address", "")) {
            dispatch(setImportAssetOne(importAssetTwo));
            dispatch(setImportAssetTwo(importAssetOne));
          } else {
            dispatch(setImportAssetOne(token));
          }
          dispatch(setModal("ImportPosition"));
          break;
        case "importAssetTwo":
          if (_get(token, "address", "") === _get(importAssetOne, "address", "")) {
            dispatch(setImportAssetOne(importAssetTwo));
            dispatch(setImportAssetTwo(importAssetOne));
          } else {
            dispatch(setImportAssetTwo(token));
          }
          dispatch(setModal("ImportPosition"));
          break;
        default:
          break;
      }
      return;
    }
  };
  const updateTokens = () => {
    setTokens(base_tokens.concat(custom_tokens));
  };

  // common tokens (the routing bases), with the token list data for their logos
  const tokenList = base_tokens.concat(custom_tokens);
  const commonTokens = _get(CONFIG_BASE, "tokens.trade", []).map(
    (token) => tokenList.find((t) => t.address === token.address) || token
  );

  // effects
  useEffect(updateTokens, [currentModal === "SelectToken"]);

  return (
    <Modal open={currentModal === "SelectToken"} onClose={closeModal} sx={{ display: "flex", p: 2 }}>
      <Box
        sx={{
          width: "100%",
          maxWidth: 420,
          height: "min(640px, calc(100vh - 32px))",
          m: "auto",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <ModalHeader title="Select a token" closeFunction={closeModal} />
        <Box sx={{ px: 2.5, pb: 2 }}>
          <TextField
            id="outlined-search"
            placeholder="Search name or paste address"
            type="search"
            fullWidth
            defaultValue={inputSearch}
            onChange={search}
            slotProps={{
              htmlInput: { "aria-label": "search name or paste address" },
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: "text.secondary" }} />
                  </InputAdornment>
                ),
              },
            }}
          />
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1.5 }}>
            {commonTokens.map((token) => (
              <Chip
                key={token.address}
                variant="outlined"
                avatar={<TokenLogo token={token} size={22} />}
                label={token.symbol}
                onClick={(e) => onSelect(e, token)}
              />
            ))}
          </Box>
        </Box>
        <List sx={{ flex: 1, overflowY: "auto", borderTop: 1, borderColor: "divider", py: 1 }}>
          <AdvertisementSlot />
          {tokens.map((token, key) => (
            <TokenListItem key={key} token={token} onClick={(e) => onSelect(e, token)} />
          ))}
          {tokens.length === 0 ? (
            <Typography sx={{ color: "text.secondary", textAlign: "center", px: 3, py: 4 }}>
              No tokens match this search. Paste a token address in Manage tokens to add it.
            </Typography>
          ) : null}
        </List>
        <Box sx={{ p: 1.5, borderTop: 1, borderColor: "divider" }}>
          <Button fullWidth startIcon={<TuneIcon />} onClick={() => dispatch(setModal("ManageToken"))}>
            Manage tokens
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default ModalSelectToken;
